import { isDefined, isUndefined, Maybe } from './MaybeV2';
import { EmDash } from './Punctuation';
import { DateTime, Interval, DurationObjectUnits } from 'luxon';
import { Translation, Translations } from './Text';

export class DateTimeRange {
    constructor(
        readonly start: DateTime,
        readonly end: DateTime,
    ) {}

    after(ref: DateTime): boolean {
        return this.start > ref && this.end > ref;
    }
}

export enum TimeUnit {
    SECOND,
    MINUTE,
    HOUR,
    DAY,
    MONTH,
    YEAR,
}

export namespace Formatter {
    export enum Locale {
        de,
        fr,
        it,
        en,
    }

    export function getLocaleFromString(s: string): Locale {
        switch (s) {
            case 'de':
                return Locale.de;
            case 'fr':
                return Locale.fr;
            case 'it':
                return Locale.it;
            case 'en':
                return Locale.en;
            default:
                return Locale.de;
        }
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function dayMonthNameYear(
        m: DateTime | string,
        locale: Locale,
    ): string {
        const dt = typeof m === 'string' ? DateTime.fromISO(m) : m;
        return dt.setLocale(Locale[locale]).toFormat('d. MMMM yyyy');
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function monthNameAbbr(dateTime: DateTime, locale: string): string {
        // we do not want to render . anywhere (e.g. French = "juil.")
        return dateTime
            .setLocale(locale.toLocaleString())
            .toFormat('LLL')
            .replace('.', '');
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function monthName(dateTime: DateTime, locale: Locale): string {
        return dateTime.setLocale(toLuxonLocale(locale)).toFormat('MMMM');
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function monthNameYear(dateTime: DateTime, locale: Locale): string {
        return dateTime.setLocale(toLuxonLocale(locale)).toFormat('MMMM yyyy');
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function isoYearMonthDay(dateTime: DateTime): string {
        const iso = dateTime.toISODate();
        if (!iso) {
            throw new Error('Invalid DateTime: toISODate() returned null');
        }
        return iso;
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function isoYearMonth(dateTime: DateTime): string {
        return dateTime.toFormat('yyyy-MM');
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function isoString(dateTime: DateTime): string {
        const iso = dateTime.toISO();
        if (!iso) {
            throw new Error('Invalid DateTime: toISO() returned null');
        }
        return iso;
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function dayMonthYear(dateTime: DateTime): string {
        return dateTime.toFormat('dd.MM.yyyy');
    }

    export function formatPermitToDateWithIndefinite(dt: DateTime): string {
        if (dt > DateTime.fromJSDate(new Date('2037-12-29'))) {
            return '∞';
        }
        return dt.toFormat('dd.MM.yyyy');
    }

    // String has to be MM-DD, manual parsing because leap years explode otherwise
    export function dayMonth(date: string): string {
        return date.substr(3, 2) + '.' + date.substr(0, 2) + '.';
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function dayMonthYearHourMinute(dateTime: DateTime): string {
        return dateTime.toFormat(`dd.MM.yyyy ${EmDash} HH:mm`);
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function dayMonthYearHourMinuteSecond(
        dateTime: string | DateTime,
    ): string {
        if (DateTime.isDateTime(dateTime)) {
            return dateTime.toFormat(`dd.MM.yyyy ${EmDash} HH:mm:ss`);
        }
        const dateTimeLuxon = DateTime.fromISO(dateTime).setLocale('de');
        const date = dateTimeLuxon.toLocaleString({
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
        const time = dateTimeLuxon.toLocaleString({
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        return `${date} ${EmDash} ${time}`;
    }

    // new Date(s) as in Parser.isoToDateTime, so null and date-only input render as before
    export const isoStringTodayMonthYearhHourMinute = (s: string): string => {
        return dayMonthYearHourMinute(DateTime.fromJSDate(new Date(s)));
    };
    export const isoStringTodayMonthYearhHourMinuteSecond = (
        s: string,
    ): string => {
        return DateTime.fromJSDate(new Date(s)).toFormat(
            `dd.MM.yyyy ${EmDash} HH:mm:ss`,
        );
    };

    /**
     * @deprecated Use dateTime instead
     */
    export function dateWithDurationFromNow(
        m: DateTime | string,
        language: string,
    ): string {
        const dt = typeof m === 'string' ? DateTime.fromISO(m) : m;
        const dateString = dayMonthYearHourMinute(dt);
        const durationString = durationFromNow(dt, language);

        return `${dateString} (${durationString})`;
    }

    export function durationFromNow(
        m: DateTime | string,
        language: string,
    ): string {
        const dt = typeof m === 'string' ? DateTime.fromISO(m) : m;
        return dt.setLocale(language).toRelative() ?? '';
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function hourMinute(dateTime: DateTime): string {
        return dateTime.toFormat('HH:mm');
    }

    /**
     * @deprecated Use dateTime instead
     */
    export const openRange = <T>(
        from: Maybe<T>,
        to: Maybe<T>,
        formatter: (t: NoInfer<T>) => string,
        placeholder = '',
    ): string => {
        if (isUndefined(from) && isUndefined(to)) {
            return '–';
        }
        return (
            (isDefined(from) ? formatter(from) : placeholder) +
            ' – ' +
            (isDefined(to) ? formatter(to) : placeholder)
        );
    };

    export function toDateTimeLocalInput(dateTime: DateTime): string {
        const iso = dateTime
            .toLocal()
            .set({ second: 0, millisecond: 0 })
            .toISO({
                includeOffset: false,
                suppressMilliseconds: true,
                suppressSeconds: true,
            });
        if (!iso) {
            throw new Error('Invalid DateTime: toISO() returned null');
        }
        return iso;
    }

    export function fromDateTimeLocalInput(
        localDateTimeInput: string,
    ): DateTime {
        return DateTime.fromISO(localDateTimeInput, {
            zone: DateTime.local().zone,
        });
    }

    export interface DurationTexts {
        seconds: Translation;
        minutes: Translation;
        hours: Translation;
        days: Translation;
        months: Translation;
        years: Translation;
        minuteFull: Translation;
        hourFull: Translation;

        [key: string]: () => string;
    }

    export const durationTexts: Translations<DurationTexts> = {
        en: {
            seconds: () => 's',
            minutes: () => 'min',
            hours: () => 'h',
            days: () => 'd',
            months: () => 'm',
            years: () => 'y',
            minuteFull: () => 'minutes',
            hourFull: () => 'hours',
        },
        de: {
            seconds: () => 's',
            minutes: () => 'min',
            hours: () => 'h',
            days: () => 'd',
            months: () => 'Mt',
            years: () => 'J',
            minuteFull: () => 'Minuten',
            hourFull: () => 'Stunden',
        },
        fr: {
            seconds: () => 's',
            minutes: () => 'min',
            hours: () => 'h',
            days: () => 'j',
            months: () => 'm',
            years: () => 'a',
            minuteFull: () => 'minutes',
            hourFull: () => 'heures',
        },
        it: {
            seconds: () => 's',
            minutes: () => 'min',
            hours: () => 'h',
            days: () => 'g',
            months: () => 'm',
            years: () => 'a',
            minuteFull: () => 'minuti',
            hourFull: () => 'ore',
        },
    };

    export function getDurationFromMinutes(
        minutes: number,
        texts: DurationTexts,
    ): string {
        const now = DateTime.now();
        return getDuration(now, now.plus({ minutes }), texts);
    }

    /*
     returns the duration as component objects
     special case: if the time difference is less than 1 minute, 1 minute is returned
     */
    export interface DurationComponent {
        value: number;
        unit: string;
    }

    export function getDurationComponents(
        from: DateTime,
        to: DateTime,
        texts: DurationTexts,
    ) {
        const duration = Interval.fromDateTimes(
            from.set({ second: 0 }),
            to.set({ second: 0 }),
        )
            .toDuration(['years', 'months', 'days', 'hours', 'minutes'])
            .toObject();
        const components: DurationComponent[] = [];

        (
            ['years', 'months', 'days', 'hours', 'minutes'] as Array<
                keyof DurationObjectUnits
            >
        ).forEach(key => {
            const value = duration[key];
            if (
                typeof value === 'number' &&
                Number.isFinite(value) &&
                value !== 0
            ) {
                components.push({
                    value: Math.round(value),
                    unit: texts[key](),
                });
            }
        });

        // special case: the minimum returned is 1 minute
        if (components.length === 0) {
            return [{ value: 1, unit: texts.minutes() }];
        }

        return components;
    }

    /**
     * @deprecated Use dateTime instead
     */
    export function getDuration(
        from: string,
        to: string,
        texts: DurationTexts,
    ): string;
    export function getDuration(
        from: DateTime,
        to: DateTime,
        texts: DurationTexts,
    ): string;
    export function getDuration(
        from: DateTime | string,
        to: DateTime | string,
        texts: DurationTexts,
    ) {
        const fromDate =
            typeof from === 'string' ? DateTime.fromISO(from) : from;
        const toDate = typeof to === 'string' ? DateTime.fromISO(to) : to;

        return getDurationComponents(fromDate, toDate, texts)
            .map(component => component.value + component.unit)
            .join(' ');
    }
}

export namespace Parser {
    // Through new Date(): date-only strings are UTC midnight and null is the
    // epoch, as the old moment parser had it.
    export function isoToDateTime(iso: string): DateTime {
        return DateTime.fromJSDate(new Date(iso));
    }

    export function dateTimeRangeFromIsoString(
        str: string,
    ): Maybe<DateTimeRange> {
        if (!str) {
            return null;
        }
        const [from, to] = str.split('/');
        const start = DateTime.fromISO(from);
        const end = DateTime.fromISO(to ?? '');
        return start.isValid && end.isValid
            ? new DateTimeRange(start, end)
            : null;
    }
}

export function ensureIso(dateTime: DateTime): string {
    const iso = dateTime.toISO();
    if (!iso) {
        throw new Error('Invalid DateTime: toISO() returned null');
    }
    return iso;
}

export function ensureIsoDate(dateTime: DateTime): string {
    const iso = dateTime.toISODate();
    if (!iso) {
        throw new Error('Invalid DateTime: toISODate() returned null');
    }
    return iso;
}

export function compareDateTime(a: DateTime, b: DateTime) {
    return a < b ? -1 : a > b ? 1 : 0;
}

export const unlimitedFromDate = DateTime.fromISO('1970-01-01').startOf('day');

export const isUnlimitedFromDate = (d: DateTime) =>
    d.hasSame(unlimitedFromDate, 'day');

export const unlimitedToDate = DateTime.fromISO('2037-12-31').endOf('day');

export const isUnlimitedToDate = (d: DateTime) =>
    d.hasSame(unlimitedToDate, 'day');

export function toLuxonLocale(locale: Formatter.Locale): string {
    switch (locale) {
        case Formatter.Locale.de:
            return 'de-CH';
        case Formatter.Locale.fr:
            return 'fr-CH';
        case Formatter.Locale.it:
            return 'it-CH';
        case Formatter.Locale.en:
            return 'en-CH';
    }
}
